import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { VoiceCardItem } from "./VoiceCardItem";
import { slideReveal } from "./Autopilot.styled";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { parseScriptBySlides } from "pages/media/projects/components/slides/";
import { parseScript } from "pages/media/helpers/scriptAnchors";

interface VoiceoverVisualProps {
  isActive: boolean;
  isDone: boolean;
  onComplete: () => void;
  sections: SectionWithSlides[];
  autoScrollAllowed?: { readonly current: boolean };
}

interface VoiceoverItem {
  slideId: string;
  text: string;
}

export const VoiceoverVisual: React.FC<VoiceoverVisualProps> = ({
  isActive,
  isDone,
  onComplete,
  sections,
  autoScrollAllowed,
}) => {
  const [visibleCount, setVisibleCount] = useState(1);
  // const [playingChapter, setPlayingChapter] = useState<number | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isActiveRef = useRef(isActive);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    isActiveRef.current = isActive;
    onCompleteRef.current = onComplete;
  });

  const items = useMemo((): VoiceoverItem[] => {
    const result: VoiceoverItem[] = [];
    for (const section of sections) {
      const scriptBySlide = parseScriptBySlides(section.script.content);
      for (const slide of section.slides) {
        const raw = scriptBySlide.get(slide.uuid);
        if (raw) {
          const text = parseScript(raw).text;
          result.push({ slideId: slide.uuid, text });
        }
      }
    }
    return result;
  }, [sections]);

  const handleTypingDone = () => {
    const next = Math.min(visibleCount + 1, items.length);
    const isLast = next >= items.length;
    setVisibleCount(next);

    // Complete synchronously so background tabs aren't blocked by rAF
    if (isLast) {
      onCompleteRef.current();
    }

    // Scroll is cosmetic — safe to keep inside rAF
    requestAnimationFrame(() => {
      if (isActiveRef.current && rowRef.current) {
        rowRef.current.scrollTo({
          left: rowRef.current.scrollWidth,
          behavior: "smooth",
        });
      }
      const canAutoScroll =
        isActiveRef.current && autoScrollAllowed?.current !== false;
      if (!isLast && canAutoScroll) {
        bottomRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    });
  };

  if (!isActive && !isDone) return null;
  if (items.length === 0) return null;

  const displayCount = isDone ? items.length : visibleCount;

  return (
    <VoiceoverRow ref={rowRef}>
      {items.map((item, i) => {
        if (i >= displayCount) return null;
        const chapter = i + 1;
        return (
          <VoiceCardItem
            key={item.slideId}
            chapter={chapter}
            text={item.text}
            // isPlaying={playingChapter === chapter}
            // onToggle={() =>
            //   setPlayingChapter((prev) => (prev === chapter ? null : chapter))
            // }
            onTypingDone={i === visibleCount - 1 ? handleTypingDone : () => {}}
            skipTyping={isDone}
          />
        );
      })}
      <div ref={bottomRef} />
    </VoiceoverRow>
  );
};

const VoiceoverRow = styled.div`
  padding: 14px 15px 16px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  animation: ${slideReveal} 0.3s ease both;
`;
